import { Injectable } from '@angular/core';

import {
  AggregatedStatusWebsocketResponse,
  FeatureDeprecated,
  FeatureNames,
  SmartNotesWebsocketResponse,
  WebsocketDcFeaturesEvent,
} from '@vi-iot/client';
import { LogType, ViLogger } from '@vi-iot/ui';

import {
  ActiveErrorsStore,
  ACTIVE_ERROR_TYPE,
  AggregatedStatusService,
  FeatureService,
} from '@store';

import { SocketIoService } from '../socket-io/socket-io.service';
import { InstallationFeaturesStore } from '../../@store/installation-features/installation-features.store';
import { bufferTime } from 'rxjs/operators';

enum FeatureType {
  Device,
  Gateway,
  OperatingModes,
  Rooms,
  RemoteDevice,
  Hems,
  Other,
}

@Injectable({
  providedIn: 'root',
})
export class LiveUpdatesDataService {
  constructor(
    private socketService: SocketIoService,
    private featureService: FeatureService,
    private aggregatedStatusService: AggregatedStatusService,
    private activeErrorsStore: ActiveErrorsStore,
    private logger: ViLogger,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  subscribe(): void {
    this.socketService.websocketFeatureData$.subscribe(
      (rawResponse: { feature: FeatureDeprecated }) => {
        const feature = rawResponse.feature as
          | FeatureDeprecated
          | { features: { [key: string]: FeatureDeprecated } };

        if ((feature as FeatureDeprecated).feature) {
          this.updateFeature(feature as FeatureDeprecated);
        } else {
          const features = (feature as { features: { [key: string]: FeatureDeprecated } }).features;
          for (const key in features) {
            if (features[key]) {
              this.updateFeature(features[key]);
            }
          }
        }
      }
    );

    this.socketService.websocketFeatureData$
      .pipe(
        bufferTime(1500) // buffer messages every 1500 milliseconds
      )
      .subscribe((rawResponse: WebsocketDcFeaturesEvent[]) => {
        if (Array.isArray(rawResponse) && rawResponse.length !== 0) {
          const features = rawResponse.map((event) => event.feature);
          this.installationFeaturesStore.upsertManyFeatures(features);
        }
      });

    this.socketService.websocketSmartNotesData$.subscribe(
      (rawResponse: SmartNotesWebsocketResponse) => {
        if (
          rawResponse.note.type === ACTIVE_ERROR_TYPE.DEVICE_ERROR ||
          rawResponse.note.type === ACTIVE_ERROR_TYPE.SYSTEM_LOCKED ||
          rawResponse.note.type === ACTIVE_ERROR_TYPE.POWER_LIMITATION_CONSUMPTION ||
          rawResponse.note.type === ACTIVE_ERROR_TYPE.POWER_LIMITATION_POWER_PRODUCTION
        ) {
          this.activeErrorsStore.liveUpdateActiveError(rawResponse);
        }
      }
    );

    this.socketService.websocketAggregatedStatusData$.subscribe(
      (aggregatedStatusResponse: AggregatedStatusWebsocketResponse) => {
        const isAggregatedStatus = !!(
          aggregatedStatusResponse?.aggregatedStatus && aggregatedStatusResponse?.lastChangedAt
        );

        if (isAggregatedStatus) {
          this.updateAggregatedStatus(aggregatedStatusResponse);
        }
      }
    );
  }

  unsubscribeAll(): void {
    this.socketService.disconnect();
  }

  private updateAggregatedStatus(entity: AggregatedStatusWebsocketResponse): void {
    this.logger.log('WEBSOCKET (Installation Aggeregated Status)', LogType.websocket);
    this.logger.log(entity, LogType.websocket);

    this.aggregatedStatusService.liveUpdateAggregatedStatus(
      entity.gatewaySerial,
      entity.aggregatedStatus,
      entity.lastChangedAt
    );

    this.aggregatedStatusService.liveUpdateAggregatedStatusOverview(
      entity.aggregatedStatus,
      entity.lastChangedAt,
      entity.gatewaySerial
    );
  }

  private updateFeature(feature: FeatureDeprecated): void {
    const featureType = this.getFeatureType(feature);

    switch (featureType) {
      case FeatureType.OperatingModes:
        this.logger.log('WEBSOCKET (heating device feature & operating mode)', LogType.websocket);
        this.logger.log(feature, LogType.websocket);
        this.featureService.liveUpdateOperatingMode(feature.feature, feature, feature.gatewayId);
        // this.featureService.liveUpdateDeviceFeature(feature.feature, feature); this is connected? why?
        break;
      default:
        this.logger.log('WEBSOCKET: type default', LogType.websocket);
        this.logger.log(feature, LogType.websocket);

        return;
    }
  }

  private getFeatureType(feature: FeatureDeprecated): FeatureType {
    const featureNameSegments = feature.feature.split('.');
    const firstSegment = featureNameSegments[0];

    // Backend returns us data we have to sort ourselves. There is unfortunately no guarantee sorting pattern covers all cases.
    const isGatewayFeature =
      firstSegment === 'gateway' || feature.feature === FeatureNames.thermalCockpit;
    const isRemoteDeviceFeature =
      firstSegment === 'trv' ||
      firstSegment === 'cs' ||
      firstSegment === 'device' ||
      firstSegment === 'fht';
    const isRoomsFeature = firstSegment === 'rooms';
    const isHemsFeature = firstSegment === 'ems';
    const isDeviceFeature =
      firstSegment === 'ventilation' ||
      firstSegment === 'heating' ||
      firstSegment === 'photovoltaic' ||
      firstSegment === 'ess' ||
      firstSegment === 'pcc';
    const isOperatingModeFeature = feature.feature.includes('operating.modes');

    if (isGatewayFeature) {
      return FeatureType.Gateway;
    } else if (isOperatingModeFeature) {
      return FeatureType.OperatingModes;
    } else if (isRemoteDeviceFeature) {
      return FeatureType.RemoteDevice;
    } else if (isRoomsFeature) {
      return FeatureType.Rooms;
    } else if (isHemsFeature) {
      return FeatureType.Hems;
    } else if (isDeviceFeature) {
      return FeatureType.Device;
    } else {
      return FeatureType.Other;
    }
  }
}
